import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Eye, EyeOff, Mail, Lock, X } from "lucide-react";
import axios from "axios";
import { api, useAuth } from "../contexts/AuthContext";

const LOGO_URL = "https://customer-assets-jt897jd0.emergentagent.net/job_5309bd56-751f-4453-bda3-7be8ecfcb781/artifacts/orcvyuj1_let%20see%20whos%20on.png";

function formatError(detail) {
  if (!detail) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

// ── Policy modal ──────────────────────────────────────────────────────────────
function PolicyModal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
        className="bg-[#0E0E17] border border-purple-500/20 rounded-2xl w-full max-w-lg max-h-[80vh] flex flex-col shadow-2xl"
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-purple-500/15 shrink-0">
          <h2 className="font-bold text-white font-[Outfit]">{title}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 overflow-y-auto text-sm text-slate-300 space-y-4 scrollbar-thin leading-relaxed">
          {children}
        </div>
      </motion.div>
    </div>
  );
}

export default function AuthPage({ mode = "login" }) {
  const navigate = useNavigate();
  const { login } = useAuth();
  const isLogin = mode === "login";

  const [form, setForm] = useState({ email: "", password: "", confirm: "" });
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [capacity, setCapacity] = useState(null); // {is_full, count, max, spots_remaining}

  // Fetch registration capacity on the register page
  useEffect(() => {
    if (!isLogin) {
      axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/public/capacity`)
        .then(r => setCapacity(r.data))
        .catch(() => {});
    }
  }, [isLogin]);

  const handleChange = (e) => {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isLogin && form.password !== form.confirm) {
      setError("Passwords don't match");
      return;
    }
    if (!isLogin && !ageConfirmed) {
      setError("You must confirm you are 18 or over to join.");
      return;
    }
    setLoading(true);
    try {
      const endpoint = isLogin ? "/auth/login" : "/auth/register";
      const { data } = await api.post(endpoint, { email: form.email, password: form.password });
      login(data);
      navigate(data.onboarding_complete ? "/lobby" : "/onboarding", { replace: true });
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/lobby";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <>
    <div className="min-h-screen bg-[#07070B] flex">
      {/* Left branding panel (desktop) */}
      <div className="hidden lg:flex flex-col items-center justify-center w-1/2 bg-[#0E0E17] border-r border-purple-500/15 relative overflow-hidden p-12">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(139,92,246,0.1)_0%,transparent_70%)]" />
        <div className="relative z-10 text-center">
          <img
            src={LOGO_URL}
            alt="Let's Check Who's On?"
            className="w-72 h-auto object-contain mx-auto mb-6 drop-shadow-[0_0_40px_rgba(139,92,246,0.6)]"
          />
          <p className="text-slate-400 text-lg max-w-xs leading-relaxed">
            Real-time chatrooms. Real people. Right now.
          </p>
          <p className="text-sm text-slate-500 mt-2">Real-Time Social Rooms · 18+</p>
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          {/* Mobile logo */}
          <div className="text-center mb-8 lg:hidden">
            <img
              src={LOGO_URL}
              alt="Let's Check Who's On?"
              className="w-44 h-auto object-contain mx-auto drop-shadow-[0_0_20px_rgba(139,92,246,0.5)]"
            />
          </div>

          <div className="bg-[#0E0E17] border border-purple-500/20 rounded-2xl p-8">

            {/* ── Registration full state ── */}
            {!isLogin && capacity?.is_full ? (
              <div className="text-center py-4">
                <div className="w-14 h-14 mx-auto rounded-2xl bg-gradient-to-br from-violet-600/20 to-cyan-500/20 border border-purple-500/25 flex items-center justify-center text-2xl mb-5">
                  🔒
                </div>
                <h2 className="text-2xl font-black text-white font-[Outfit] mb-2">
                  Founding Members Full
                </h2>
                <p className="text-slate-400 text-sm leading-relaxed mb-1">
                  Our private test run is at capacity.
                </p>
                <p className="text-slate-500 text-sm mb-6">
                  {capacity.max} founding members enrolled · We'll open to more soon.
                </p>
                <p className="text-slate-400 text-sm mb-4">
                  Already a founding member?
                </p>
                <button
                  onClick={() => navigate("/login")}
                  className="w-full py-3 bg-gradient-to-r from-violet-600 to-cyan-500 text-white font-bold rounded-xl hover:opacity-90 active:scale-95 transition-all"
                >
                  Sign In
                </button>
              </div>
            ) : (
              <>
                <h2 className="text-2xl font-bold text-white mb-1 font-[Outfit]">
                  {isLogin ? "Welcome back" : "Join the conversation"}
                </h2>
                <p className="text-slate-400 text-sm mb-1">
                  {isLogin ? "Sign in to see who's on" : "Create your account. No real name needed."}
                </p>
                {/* Spots remaining pill — only show on register when not full */}
                {!isLogin && capacity && !capacity.unlimited && !capacity.is_full && (
                  <div className="flex items-center gap-2 mb-5">
                    <span className="inline-flex items-center gap-1.5 text-xs font-mono bg-emerald-500/10 border border-emerald-500/25 text-emerald-400 px-2.5 py-1 rounded-full">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                      {capacity.spots_remaining} of {capacity.max} founding member spot{capacity.spots_remaining !== 1 ? "s" : ""} remaining
                    </span>
                  </div>
                )}

            {/* Google button */}
            <button
              data-testid="google-auth-btn"
              onClick={handleGoogle}
              className="w-full py-3 bg-white text-gray-800 rounded-xl font-semibold text-sm flex items-center justify-center gap-3 hover:bg-gray-100 transition-colors mb-4"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </button>

            <div className="flex items-center gap-3 mb-4">
              <div className="flex-1 h-px bg-slate-700" />
              <span className="text-xs text-slate-500">or</span>
              <div className="flex-1 h-px bg-slate-700" />
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Email */}
              <div>
                <label className="block text-xs text-slate-400 mb-1.5 font-medium uppercase tracking-wide">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <input
                    data-testid="email-input"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    required
                    placeholder="you@example.com"
                    className="w-full bg-[#141322] border border-slate-700 rounded-xl py-3 pl-10 pr-4 text-white text-sm placeholder-slate-600 focus:outline-none focus:border-purple-500 transition-colors"
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <label className="block text-xs text-slate-400 mb-1.5 font-medium uppercase tracking-wide">Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <input
                    data-testid="password-input"
                    name="password"
                    type={showPw ? "text" : "password"}
                    value={form.password}
                    onChange={handleChange}
                    required
                    placeholder="••••••••"
                    className="w-full bg-[#141322] border border-slate-700 rounded-xl py-3 pl-10 pr-10 text-white text-sm placeholder-slate-600 focus:outline-none focus:border-purple-500 transition-colors"
                  />
                  <button type="button" onClick={() => setShowPw(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300">
                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Confirm password */}
              {!isLogin && (
                <div>
                  <label className="block text-xs text-slate-400 mb-1.5 font-medium uppercase tracking-wide">Confirm Password</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <input
                      data-testid="confirm-password-input"
                      name="confirm"
                      type="password"
                      value={form.confirm}
                      onChange={handleChange}
                      required
                      placeholder="••••••••"
                      className="w-full bg-[#141322] border border-slate-700 rounded-xl py-3 pl-10 pr-4 text-white text-sm placeholder-slate-600 focus:outline-none focus:border-purple-500 transition-colors"
                    />
                  </div>
                </div>
              )}

              {/* 18+ confirmation — register only */}
              {!isLogin && (
                <>
                  <label
                    data-testid="age-confirm-label"
                    className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                      ageConfirmed ? "border-purple-500/40 bg-purple-500/10" : "border-slate-700 bg-[#141322] hover:border-slate-600"
                    }`}
                  >
                    <input
                      data-testid="age-confirm-checkbox"
                      type="checkbox"
                      checked={ageConfirmed}
                      onChange={e => { setAgeConfirmed(e.target.checked); setError(""); }}
                      className="mt-0.5 w-4 h-4 accent-purple-500 shrink-0 cursor-pointer"
                    />
                    <span className="text-sm text-slate-300 leading-snug">
                      I confirm I am <span className="text-white font-semibold">18 years or older</span>.
                      WHO'S ON? is an adult platform.
                    </span>
                  </label>
                  <p className="text-center text-xs text-slate-500 leading-relaxed">
                    By joining you agree to our{" "}
                    <a href="/terms" target="_blank" rel="noopener noreferrer"
                      className="text-purple-400 hover:text-purple-300 underline underline-offset-2 transition-colors">
                      Terms &amp; Conditions
                    </a>
                    {" "}and{" "}
                    <a href="/privacy" target="_blank" rel="noopener noreferrer"
                      className="text-purple-400 hover:text-purple-300 underline underline-offset-2 transition-colors">
                      Privacy Policy
                    </a>.
                    Also see our{" "}
                    <a href="/guidelines" target="_blank" rel="noopener noreferrer"
                      className="text-purple-400 hover:text-purple-300 underline underline-offset-2 transition-colors">
                      Community Guidelines
                    </a>.
                  </p>
                </>
              )}

              {error && (
                <p data-testid="auth-error-msg" className="text-red-400 text-sm bg-red-500/10 rounded-lg px-3 py-2">
                  {error}
                </p>
              )}

              <button
                data-testid="auth-submit-btn"
                type="submit"
                disabled={loading || (!isLogin && !ageConfirmed)}
                className="w-full py-3 bg-gradient-to-r from-violet-600 to-cyan-500 text-white font-bold rounded-xl hover:opacity-90 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? "Please wait..." : isLogin ? "Sign In" : "Create Account"}
              </button>
            </form>

            <p className="text-center text-sm text-slate-500 mt-4">
              {isLogin ? "Don't have an account? " : "Already have an account? "}
              <Link
                data-testid="auth-toggle-link"
                to={isLogin ? "/register" : "/login"}
                className="text-purple-400 hover:text-purple-300 font-medium"
              >
                {isLogin ? "Join free" : "Sign in"}
              </Link>
            </p>
              </>
            )}
          </div>
        </motion.div>
      </div>
    </div>

    {/* Terms modal */}
    <AnimatePresence>
      {showTerms && (
        <PolicyModal title="Terms of Service" onClose={() => setShowTerms(false)}>
          <p className="text-slate-400 text-xs uppercase tracking-widest font-mono mb-2">Last updated · 2025</p>
          {[
            ["1. Age Requirement", "You must be 18 years or older to use WHO'S ON?. By creating an account you confirm this. We reserve the right to remove accounts that violate this rule."],
            ["2. Be Respectful", "Public chatrooms are shared spaces. Harassment, hate speech, threats, explicit content, and spam are prohibited and will result in an immediate ban."],
            ["3. Your Alias", "Your alias must not impersonate real people, brands, or other users. Aliases are unique and non-transferable."],
            ["4. Moderation", "We reserve the right to remove any content and suspend or ban any account at our discretion, with or without notice."],
            ["5. No Liability", "WHO'S ON? provides a platform for conversation. We are not responsible for user-posted content. Use the platform at your own risk."],
          ].map(([title, body]) => (
            <div key={title}>
              <h3 className="text-white font-semibold mb-1">{title}</h3>
              <p className="text-slate-400">{body}</p>
            </div>
          ))}
        </PolicyModal>
      )}
    </AnimatePresence>

    {/* Privacy modal */}
    <AnimatePresence>
      {showPrivacy && (
        <PolicyModal title="Privacy Policy" onClose={() => setShowPrivacy(false)}>
          <p className="text-slate-400 text-xs uppercase tracking-widest font-mono mb-2">Last updated · 2025</p>
          {[
            ["What we collect", "We collect your email address, chosen alias, avatar selection, and chat messages sent in public rooms. No real name is required or stored."],
            ["How we use it", "Your data is used solely to provide the WHO'S ON? service — login, displaying your alias in rooms, and moderation. We do not use data for advertising."],
            ["Google Sign-In", "If you sign in with Google, they may share your basic profile (name, email). We only use email for account identification."],
            ["We never sell your data", "Your personal data is never sold or shared with third-party advertisers or data brokers. Ever."],
            ["Your rights", "You can request full deletion of your account and all associated data at any time. We will process your request within 30 days."],
          ].map(([title, body]) => (
            <div key={title}>
              <h3 className="text-white font-semibold mb-1">{title}</h3>
              <p className="text-slate-400">{body}</p>
            </div>
          ))}
        </PolicyModal>
      )}
    </AnimatePresence>
    </>
  );
}
